import { Injectable, InjectionToken, inject } from '@angular/core';
import { OktaAuth, SignoutOptions } from '@okta/okta-auth-js';
import { WindowService } from '@drc-eca/eca-components-lib';
import { environment } from '../../../environments/environment';
export const OKTA_AUTH_CLIENT = new InjectionToken<any>('OKTA_AUTH_CLIENT');

@Injectable({
  providedIn: 'root'
})
export class OktaService {
  private oktaAuthClient = inject<OktaAuth>(OKTA_AUTH_CLIENT);
  private windowService = inject(WindowService);

  isUserLoggedIn(): Promise<boolean> {
    return this.oktaAuthClient.session.exists();
  }

  signIn(credentials: any) {
    return this.oktaAuthClient.signIn(credentials);
  }

  setCookieAndRedirect(sessionToken: string, redirectUrl?: string) {
    if (redirectUrl) {
      return this.oktaAuthClient.session.setCookieAndRedirect(sessionToken, redirectUrl);
    }
    return this.oktaAuthClient.session.setCookieAndRedirect(sessionToken);
  }

  sessionExists() {
    return this.oktaAuthClient.session.exists();
  }

  getTokens(scopes: string[]) {
    return this.oktaAuthClient.token.getWithoutPrompt({ scopes });
  }

  /**
   * Tells Okta to logout
   * @param postLogoutRedirectUri URI from the Okta dashboard
   * @param state The state to return to. If any URL be sure to have it encoded before sending. The state is returned as a csv (state=csv)
   */
  async logout(postLogoutRedirectUri?: string, state: string[] = []): Promise<void> {
    if (environment.useNewLoginPage) {
      try {
        await this.oktaAuthClient.stop();
      } catch (e) {
        console.error('stop: ', e);
      }
      try {
        const opts = {
          revokeAccessToken: true,
          revokeRefreshToken: true,
          clearTokensBeforeRedirect: true,
        } as SignoutOptions;
        if (postLogoutRedirectUri) {
          opts.postLogoutRedirectUri = postLogoutRedirectUri;
        }
        if (state.length) {
          opts.state = state.join(',');
        }
        await this.oktaAuthClient.signOut(opts);
      } catch (e) {
        console.error('signOut: ', e);
      }
    } else {
      if (await this.isUserLoggedIn()) {
        await this.oktaAuthClient.revokeAccessToken();
        await this.oktaAuthClient.closeSession();
      }
    }
  }

  async signOutOfTenant(orgOrigin: string, fromUri: string, state: string[] = []): Promise<void> {
    const fromUriUrl = new URL(fromUri);
    if (state.length) {
      fromUriUrl.searchParams.set('state', state.join(','));
    }
    const signOutUrl = `${orgOrigin}/login/signout?fromURI=${encodeURIComponent(fromUriUrl.toString())}`;
    this.windowService.navigateTo(signOutUrl);
  }

  async closeSession(): Promise<void> {
    try {
      await this.oktaAuthClient.closeSession();
    } catch {
      // Session may already be closed or expired
    }
  }
}

